import Modal from '@/customComponents/uiComponents/modal/Modal.jsx'
import ModalHeader from '@/customComponents/uiComponents/modal/ModalHeader.jsx'
import {arrowRightSm} from '@img/index.js'

/**
 * THE TWO ENDS OF THE WALK, AS PICTURES — where you are now, and where you are going.
 *
 * ── ⚠️ THE PHOTOGRAPH IS THE LABEL ────────────────────────────────────────────────
 * "International Arrivals / Customs" is a name a passenger has to translate into a place.
 * A picture of the actual hall is the same information with the translation already done,
 * which is why the thumbnail is the control and the words sit under it rather than the
 * other way round.
 *
 * ── ⚠️ AND THE CAPTION SAYS THE ROLE, NOT THE NAME ────────────────────────────────
 * Under the thumbs it is "Your Location" and "Pickup Point" — which end of the walk this
 * is — because that is the only thing being asked at a glance. The real names are long
 * enough to wrap to two lines under a 64px box, and at that size a wrapped proper noun
 * reads as clutter around the picture that was supposed to be the point. The full name is
 * the title of the photograph when it is opened.
 *
 * ── ⚠️ ONE END IS ALWAYS A GUESS ──────────────────────────────────────────────────
 * The START photo needs the flight's scope, which comes from a lookup that can be pending,
 * missing or wrong. With no scope there is no honest photograph to show, so the thumb
 * falls back to a pin on grey — a blank frame rather than a picture of the wrong hall.
 */

/** One end: a photo (or a pin), with what it is underneath. */
function PlaceThumb({photo, caption, onOpen}) {
    /* Only a thumb with a photograph has anything to open — a placeholder that opened a
       dialog to show the same pin larger is a control that does nothing. */
    const interactive = !!photo && !!onOpen

    return (
        <div className="flex min-w-0 flex-1 flex-col items-center gap-1.5">
            <button
                type="button"
                onClick={interactive ? onOpen : undefined}
                disabled={!interactive}
                aria-label={interactive ? `View ${caption}` : caption}
                className={`h-16 w-full overflow-hidden rounded-(--radius-lg) border-0 bg-(--bg-grey) p-0 shadow-[0_4px_14px_rgba(10,10,10,0.1)] ${
                    interactive ? 'cursor-pointer hover:opacity-90' : 'cursor-default'
                }`}
            >
                {photo ? (
                    <img src={photo} alt={caption} className="h-full w-full object-cover"/>
                ) : (
                    <span className="flex h-full w-full items-center justify-center text-(--text-secondary)">
                        <svg className="h-5 w-5 max-md:h-4 max-md:w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M12 21s7-5.7 7-11a7 7 0 1 0-14 0c0 5.3 7 11 7 11z"/>
                            <circle cx="12" cy="10" r="2.5"/>
                        </svg>
                    </span>
                )}
            </button>
            {/* One line, always — see the note above. */}
            <span className="w-full truncate text-center text-xs font-medium text-(--text-primary)">
                {caption}
            </span>
        </div>
    )
}

export default function PlaceReferenceRow({plan, onOpen}) {
    return (
        <div className="flex w-full items-start gap-2.5">
            <PlaceThumb photo={plan.startPhoto} caption="Your Location" onOpen={() => onOpen('start')}/>
            {/* Nudged down so it sits level with the thumbnails rather than with the
                captions under them. */}
            <img src={arrowRightSm} alt="" className="mt-6 h-4 w-4 shrink-0 opacity-40"/>
            <PlaceThumb photo={plan.endPhoto} caption="Pickup Point" onOpen={() => onOpen('end')}/>
        </div>
    )
}

/**
 * THE PHOTOGRAPH, FULL SIZE — its own dialog over the walk.
 *
 * ── ⚠️ A DIALOG, NOT A PANEL THAT TAKES THE STAGE'S PLACE ─────────────────────────
 * It used to swap itself in where the drawing was, which put a photograph into a box sized
 * for a diagram: the picture was cropped, the description ran past the bottom edge, and
 * the walk vanished while someone was only trying to check a doorway. A passenger doing
 * that wants the picture as big as the screen allows and the route still there when they
 * close it — which is what a dialog gives and an inline swap cannot.
 *
 * ── ⚠️ `object-contain`, NEVER `cover` ────────────────────────────────────────────
 * This is a reference photograph of a real doorway. Cropping it to fill a box is exactly
 * the wrong trade here — the blue "A" sign or the arrivals board a passenger is matching
 * against is as likely to be at an edge as in the middle.
 */
export function PlaceLightbox({open, onClose, photo, title, description}) {
    return (
        <Modal isOpen={open} onClose={onClose} flushBody>
            <ModalHeader title={title} onClose={onClose}/>
            <div className="flex flex-col gap-4 px-6 pb-6 lg:px-8 lg:pb-8 max-md:gap-3 max-md:px-4">
                {photo && (
                    /* Capped in viewport units so a tall photo cannot push the description
                       off the bottom of the sheet on a short window. */
                    <img
                        src={photo}
                        alt={title}
                        className="max-h-[55dvh] w-full rounded-(--radius-lg) bg-(--bg-grey) object-contain"
                    />
                )}
                <p className="mb-0! text-(--text-secondary)">{description}</p>
            </div>
        </Modal>
    )
}
